import {
  distanceBetweenCoordinates,
  isDateInRange,
  lngLatToXY,
  splitArray,
  vecFromLatLong,
} from '@core/logic';
import {
  ascQualitySortedGeoPositionSolutions,
  type GeoPositionSolution,
  type Photo,
} from '@photos/types';
import type { DateRange } from '@projects/pages/detail/hooks';
import type { BoundingBox } from '@viewer3D/types';
import { DateTime } from 'luxon';
import type { Vector3 } from 'three';
import {
  areAllCategoriesSelected,
  type Classification,
  type Category,
  areAllClassificationsSelected,
  areAllScannersSelected,
  type Scanner,
} from './state';

interface PhotoWithPosition extends Photo {
  position: Vector3;
}

const sortByGeoPositionSolution = (cluster: readonly Photo[]): Photo[] =>
  [...cluster].sort(
    (photoCurrent, photoNext) =>
      ascQualitySortedGeoPositionSolutions.indexOf(photoNext.geoPositionSolution) -
      ascQualitySortedGeoPositionSolutions.indexOf(photoCurrent.geoPositionSolution),
  );

export const gnssColor: Record<GeoPositionSolution, 'primary' | 'error' | 'info'> = {
  FIXED: 'primary',
  FLOAT: 'info',
  CORELOCATION: 'error',
};

const splitSinglesAndClusters = (photoGroups: Photo[][]): [Photo[], Photo[][]] => {
  const [withOnlyOneItem, withMultipleItems] = splitArray(photoGroups, (p) => p.length === 1);
  return [withOnlyOneItem.flat(), withMultipleItems];
};

export const withPhotoPosition = (photo: Photo): PhotoWithPosition => ({
  ...photo,
  position: vecFromLatLong(photo.lat, photo.long, 0),
});

export const getPhotoClusters = (
  photos: readonly Photo[],
  threshold: number,
): [Photo[], Photo[][]] => {
  const sortedPhotos = sortByGeoPositionSolution(photos);
  let photosWithPosition = sortedPhotos.map(withPhotoPosition);
  const groups: Photo[][] = [];

  while (photosWithPosition.length) {
    const [photo] = photosWithPosition;
    const [cluster, nextRest] = splitArray(
      photosWithPosition,
      (p) => distanceBetweenCoordinates(photo.position, p.position) < threshold,
    );
    photosWithPosition = nextRest;
    groups.push(cluster);
  }

  return splitSinglesAndClusters(groups);
};

const isPhotoInCategories = (categories: readonly Category[]) => (p: Photo) =>
  areAllCategoriesSelected(categories) ||
  (p.category?.name && categories.includes(p.category?.name));

const isPhotoInClassifications = (classifications: readonly Classification[]) => (p: Photo) => {
  if (areAllClassificationsSelected(classifications)) {
    return true;
  }

  if (classifications.includes('All others')) {
    return p.classification?.objectName !== 'Ruler';
  }

  return (
    p.classification && classifications.includes(p.classification.objectName as Classification)
  );
};

const isPhotoWithScanners = (scanners: readonly Scanner[]) => (p: Photo) => {
  if (areAllScannersSelected(scanners)) {
    return true;
  }

  return scanners.some((s) => s.id === p.deviceId);
};

const isPhotoInDateRange =
  (dateRange?: DateRange) =>
  (photo: Photo): boolean => {
    // TODO use same fn when filtering scans too! (see useScansInScope())
    if (!dateRange) return true;
    const normalizedRecordedAt = DateTime.fromISO(photo.recordedAt).toISODate()!;
    return isDateInRange(dateRange.from, dateRange.to, normalizedRecordedAt);
  };

const isPhotoWithinBbox =
  (bbox: BoundingBox) =>
  ({ long, lat }: Photo): boolean => {
    const { x, y } = lngLatToXY({ lng: long, lat: lat });
    return bbox.minY <= y && y <= bbox.maxY && bbox.minX <= x && x <= bbox.maxX;
  };

const hasPhotos = (cluster: Photo[]) => cluster.length > 0;

/**
 * Filter photos by category, bounding box and date range or any combination of these parameters.
 * (This can filter photos by bounding box AND date range, which is currently not a use case, actually. But it was the simpler implementation instead of mutually exclusive bounding box XOR date range parameters.)
 */
export const getClustersAndSinglesForViewport = (
  photosToCluster: readonly Photo[],
  viewport: BoundingBox,
  selectedCategories: readonly Category[],
  selectedClassifications: readonly Classification[],
  selectedScanners: readonly Scanner[],
): { singles: Photo[]; clusters: Photo[][] } => {
  const photosInViewport = photosToCluster.filter(
    (photo) =>
      isPhotoInCategories(selectedCategories)(photo) &&
      isPhotoWithinBbox(viewport)(photo) &&
      isPhotoInClassifications(selectedClassifications)(photo) &&
      isPhotoWithScanners(selectedScanners)(photo),
  );

  const thresholdInMeter = 1;
  const [singles, clusters] = getPhotoClusters(photosInViewport, thresholdInMeter);
  return { singles, clusters: clusters.filter(hasPhotos) };
};

export const getFilteredPhotosByDateRange = (
  allPhotos: readonly Photo[],
  dateRange?: DateRange,
): Photo[] => {
  return allPhotos.filter((photo) => isPhotoInDateRange(dateRange)(photo));
};

export const getFilteredPhotosByBbox = (
  allPhotos: readonly Photo[],
  viewport: BoundingBox,
): Photo[] => {
  return allPhotos.filter((photo) => isPhotoWithinBbox(viewport)(photo));
};
